import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Coins01Icon } from "@hugeicons/core-free-icons";
import { useSidebar } from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useCreditsBalance } from "@/lib/hooks/use-credits";

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  max: "Max",
};

export function SidebarCreditsWidget() {
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { data: balance } = useCreditsBalance();

  if (!balance) return null;

  const pct =
    balance.plan_credits_monthly > 0
      ? Math.min(100, Math.round((balance.total / balance.plan_credits_monthly) * 100))
      : 0;

  const planLabel = PLAN_LABELS[balance.plan] ?? balance.plan.toUpperCase();
  if (isCollapsed) {
    return (
      <div className="mx-2 mb-2 flex justify-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="group flex size-8 items-center justify-center rounded-lg border border-transparent text-muted-foreground transition-all duration-300 hover:border-brand/30 hover:bg-brand/5 hover:text-brand hover:shadow-sm"
              onClick={() => navigate("/settings?upgrade=1")}
            >
              <HugeiconsIcon
                icon={Coins01Icon}
                className="size-4 transition-transform group-hover:scale-110"
                strokeWidth={2}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium text-xs">
            {balance.total.toLocaleString()} credits
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className="group relative mx-2 mb-2 cursor-pointer overflow-hidden rounded-xl p-3 transition-all duration-300 bg-card/60 dark:bg-white/5 backdrop-blur-3xl border border-border/60 shadow-sm flex flex-col gap-3 group hover:border-border/80 hover:shadow-md hover:bg-card/20 dark:hover:bg-white/8"
      onClick={() => navigate("/settings?upgrade=1")}
    >
      {/* Background glow on hover */}
      <div className="absolute inset-0 bg-linear-to-br from-brand/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-brand/10" />

      <div className="relative z-10 flex flex-col gap-3">
        {/* Header row: Icon/Label and Upgrade button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {/* <div className="flex size-7 items-center justify-center rounded-full bg-brand/10 text-brand shadow-inner ring-1 ring-brand/20">
              <HugeiconsIcon
                icon={Coins01Icon}
                className="size-3.5"
                strokeWidth={2.5}
              />
            </div> */}
            <div className="flex flex-col">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {planLabel} Plan
              </span>
              <span className="text-sm font-bold leading-none tracking-tight text-foreground tabular-nums">
                {balance.total.toLocaleString()}
                <span className="text-xs font-medium text-muted-foreground/60">
                  {" "}remaining
                </span>
              </span>
            </div>
          </div>

          {/* {!isMax && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 gap-1 rounded-full border-brand/20 bg-brand/5 px-2.5 text-[10px] font-semibold text-brand transition-all hover:bg-brand hover:text-white dark:hover:bg-brand dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/settings?upgrade=1");
              }}
            >
              Manage
              <HugeiconsIcon
                icon={ArrowRight01Icon}
                className="size-3 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.5}
              />
            </Button>
          )} */}
        </div>

        {/* Progress Bar */}
        <div className="mt-0.5 h-1.5 w-full rounded-full bg-muted shadow-inner dark:bg-white/5">
          <div
            className={cn(
              "relative h-full rounded-full transition-all duration-1000 ease-out",
              "bg-gradient-to-r from-[oklch(0.72_0.22_30)] via-[oklch(0.68_0.22_350)] to-[oklch(0.62_0.22_285)]",
              "dark:from-[oklch(0.62_0.25_30)] dark:via-[oklch(0.58_0.25_350)] dark:to-[oklch(0.52_0.25_285)]",
            )}
            style={{ width: `${Math.min(pct, 100)}%` }}
          >
            {/* Inner sweep effect on group hover */}
            <div className="absolute inset-0 overflow-hidden rounded-full">
              <div className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 ease-in-out group-hover:translate-x-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
